import { useMemo, useState } from 'react';
import type { MarketSnapshot, PriceHistory, ReserveSnapshot } from '../../shared/types';
import { BRAND } from '../brand';
import { AssetName } from '../components/AssetBadge';
import { BrandMark, IconBars, IconPie, IconSearch, IconSort } from '../components/Icons';
import { Sparkline } from '../components/Sparkline';
import { Amount, Apy, Paper, ReserveTags, TopBand, TopStat } from '../components/ui';
import { assetMeta, marketCapUsd } from '../../shared/assets-meta';
import { fmtUsd, rayToApy, usdValue } from '../lib/format';
import { linkTo, navigate } from '../router';

type SortKey = 'cap' | 'symbol' | 'price' | 'supplied' | 'supplyApy' | 'borrowed' | 'borrowApy';
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'cap', label: 'Market cap' }, { key: 'symbol', label: 'Asset' }, { key: 'price', label: 'Oracle price' },
  { key: 'supplied', label: 'Total supplied' }, { key: 'supplyApy', label: 'Supply APY' },
  { key: 'borrowed', label: 'Total borrowed' }, { key: 'borrowApy', label: 'Borrow APY' },
];

export function Change({ pct }: { pct: number | null | undefined }) {
  if (pct === null || pct === undefined) return <span className="muted">–</span>;
  // Direction is carried by the sign and the arrow, colour only reinforces it.
  const dir = pct > 0.005 ? 'up' : pct < -0.005 ? 'down' : 'flat';
  return <span className="delta" data-dir={dir}>{dir === 'up' ? '▲' : dir === 'down' ? '▼' : ''} {pct > 0 ? '+' : ''}{pct.toFixed(2)}%</span>;
}

const usd = (raw: string, r: ReserveSnapshot) => usdValue(BigInt(raw), r.decimals, BigInt(r.priceUsd));

export function Markets({ market, prices }: { market: MarketSnapshot; prices?: PriceHistory }) {
  const [query, setQuery] = useState('');
  // Default order: USDG first, then the stocks by market cap (the snapshot itself is ordered the same way).
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'cap', dir: -1 });
  const totals = useMemo(() => {
    const size = market.reserves.reduce((s, r) => s + usd(r.totalSupplied, r), 0);
    const borrows = market.reserves.reduce((s, r) => s + usd(r.totalDebt, r), 0);
    return { size, borrows, available: size - borrows };
  }, [market]);

  const rows = useMemo(() => {
    const value: Record<SortKey, (r: ReserveSnapshot) => number | string> = {
      // the stablecoin is pinned above every stock; a stock without a known share count sorts below the rest
      cap: (r) => (r.role === 'borrow' ? Number.POSITIVE_INFINITY : (marketCapUsd(r.symbol, r.priceUsd) ?? -1)),
      symbol: (r) => r.symbol, price: (r) => Number(r.priceUsd), supplied: (r) => usd(r.totalSupplied, r), supplyApy: (r) => rayToApy(r.supplyRateRay),
      borrowed: (r) => usd(r.totalDebt, r), borrowApy: (r) => (r.borrowingEnabled ? rayToApy(r.borrowRateRay) : -1),
    };
    const q = query.trim().toLowerCase();
    return market.reserves
      .filter((r) => !q || r.symbol.toLowerCase().includes(q) || assetMeta(r.symbol).en.toLowerCase().includes(q))
      .sort((a, b) => { const x = value[sort.key](a), y = value[sort.key](b); return (x < y ? -1 : x > y ? 1 : 0) * sort.dir; });
  }, [market, query, sort]);

  // A header starts on the natural order for its key (A to Z for the asset, high to low for figures); a second click flips it.
  const Th = ({ k, children, num = true }: { k: SortKey; children: string; num?: boolean }) => (
    <th className={num ? 'num' : undefined} aria-sort={sort.key === k ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
      <button type="button" className="th-sort" onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? (s.dir === 1 ? -1 : 1) : k === 'symbol' ? 1 : -1 }))}>{children}<IconSort size={12} /></button>
    </th>
  );

  return (
    <>
      <TopBand title={<div className="market-title"><BrandMark size={32} /><div><h1>{BRAND.marketName}</h1><span className="pill pill--version">V3.7</span></div></div>}>
        <TopStat icon={<IconPie size={18} />} label="Total market size">{fmtUsd(totals.size, { compact: true })}</TopStat>
        <TopStat icon={<IconBars size={18} />} label="Total available">{fmtUsd(totals.available, { compact: true })}</TopStat>
        <TopStat icon={<IconBars size={18} />} label="Total borrows">{fmtUsd(totals.borrows, { compact: true })}</TopStat>
      </TopBand>
      <main className="page">
        <Paper id="market-assets" title={`${BRAND.name} assets`}
          right={
            <div className="market-tools">
              <div className="market-sort">
                <label>Sort by
                  <select value={sort.key} onChange={(e) => setSort({ key: e.target.value as SortKey, dir: e.target.value === 'symbol' ? 1 : -1 })}>
                    {SORT_OPTIONS.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
                  </select>
                </label>
                <button type="button" className="btn btn--outlined" aria-label={`Sort ${sort.dir === 1 ? 'descending' : 'ascending'}`}
                  onClick={() => setSort((s) => ({ ...s, dir: s.dir === 1 ? -1 : 1 }))}>
                  <IconSort size={14} />{sort.key === 'symbol' ? (sort.dir === 1 ? 'A to Z' : 'Z to A') : (sort.dir === 1 ? 'Low to high' : 'High to low')}
                </button>
              </div>
              <label className="search"><IconSearch size={16} /><input type="search" placeholder="Search name or symbol" aria-label="Search assets" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
            </div>
          }>
          {rows.length === 0 ? <p className="no-data">We couldn&apos;t find any assets related to your search. Try again with a different asset name or symbol.</p> : (
            <div className="table-wrap"><table className="list cards cards--market" data-sort={sort.key}>
              <thead><tr>
                <Th k="symbol" num={false}>Asset</Th><Th k="price">Oracle price</Th><th className="col-spark">Last 7 days</th><Th k="supplied">Total supplied</Th><Th k="supplyApy">Supply APY</Th>
                <Th k="borrowed">Total borrowed</Th><Th k="borrowApy">Borrow APY</Th><th className="col-actions" />
              </tr></thead>
              <tbody>{rows.map((r) => {
                const series = prices?.series[r.symbol];
                return (
                  <tr key={r.symbol} className="is-clickable" onClick={() => navigate(`/reserve/${r.symbol}`)}>
                    <td><a className="asset-link" href={`/reserve/${r.symbol}`} onClick={(e) => { e.stopPropagation(); linkTo(`/reserve/${r.symbol}`).onClick(e); }}><AssetName symbol={r.symbol} extra={<ReserveTags reserve={r} series={series} />} /></a></td>
                    <td className="num col-price" data-label="Oracle price"><span className="amount-cell"><span>{r.priceError ? '–' : fmtUsd(Number(r.priceUsd) / 1e8)}</span><small><Change pct={series?.change24hPct} /></small></span></td>
                    <td className="col-spark" data-label="Last 7 days"><Sparkline points={series?.points ?? []} label={`${r.symbol} price, last 7 days`} /></td>
                    <td className="num col-supplied" data-label="Total supplied"><Amount raw={r.totalSupplied} reserve={r} digits={2} /></td>
                    <td className="num col-supply-apy" data-label="Supply APY"><Apy value={rayToApy(r.supplyRateRay)} /></td>
                    <td className="num col-borrowed" data-label="Total borrowed">{r.borrowingEnabled ? <Amount raw={r.totalDebt} reserve={r} digits={2} /> : <span className="muted" title="Collateral only: this asset cannot be borrowed">—</span>}</td>
                    <td className="num col-borrow-apy" data-label="Borrow APY">{r.borrowingEnabled ? <Apy value={rayToApy(r.borrowRateRay)} /> : <span className="muted" title="Collateral only: this asset cannot be borrowed">—</span>}</td>
                    <td className="actions"><a className="btn btn--outlined btn--sm" {...linkTo(`/reserve/${r.symbol}`)} onClickCapture={(e) => e.stopPropagation()}>Details</a></td>
                    <td className="col-status"><ReserveTags reserve={r} series={series} /></td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </Paper>
      </main>
    </>
  );
}
